import { useEffect, useMemo, useRef, useState } from "react";
import { NavLink, useLocation, useNavigate, useSearchParams } from "react-router-dom";

import { ApplicationLayout, Notification, Sidebar } from "@rio-cloud/rio-uikit";
import RadioButton from '@rio-cloud/rio-uikit/RadioButton';
import RioMap, { ICoordinates, ISandboxMarker } from "../../components/Map";

import { Paginator } from "@components";
import { useDebounce } from "@hooks";
import Checkbox from "@rio-cloud/rio-uikit/Checkbox";
import Select from "@rio-cloud/rio-uikit/Select";
import Slider from "@rio-cloud/rio-uikit/Slider";
import SubNavigation from "@rio-cloud/rio-uikit/SubNavigation";
import TableSearch from "@rio-cloud/rio-uikit/TableSearch";
import Autosuggest from "src/components/Autosuggest";
import CustomInputSelect from "../../components/CustomInputSelect/CustomInputSelect";
import { createKmInputHandlers, createTimeInputTextHandlers, } from "../../components/ModalMapInterestArea";
import { useMapDrawingContext } from "../../providers/MapDrawingContext";
import { createAreaRework, getAreaReworkById, updateAreaRework } from "../../services/areas";
import { fetchAutosuggest, fetchRoutesList } from "../../services/routes";
import { CreateAreaReworkPayload } from "../../types/areas";
import { MarkerColor } from "@rio-cloud/rio-uikit/mapTypes";

const PAGE_SIZE = 10;
interface Props {
  onCancel: (shouldRefresh?: boolean) => void;
  areaId?: string;
}

type Route = {
  id: string;
  name: string;
  origin: string;
  destination: string;
  stops: string;
  distance: string;
  duration: string;
  register: string;
  vwcoin: string;
  performance: string;
};

const AreasCreate = ({ onCancel, areaId }: Props) => {
  const {
    radius,
    handleChangeRadius,
    drawingType,
    setDrawingType,
    drawingPoints,
    setDrawingPoints,
    type,
    setType,
    formattedAddress,
    setFormattedAddress,
    shape,
    setShape,
    selectedOption,
    setSelectedOption,
    setCircleDrawing,
    circleDrawing,
    setPolygonDrawing,
    polygonDrawing,
    setEnableDrawingClick,
    setMapZoom,
    setCenterMarker,
    centerMarker,
    setMapCenter
  } = useMapDrawingContext();

  const [routes, setRoutes] = useState<Route[]>([]);
  const [selectedRouteIds, setSelectedRouteIds] = useState<string[]>([]);
  const [pages, setPages] = useState(0);
  const [page, setPage] = useState(1);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [search, setSearch] = useState('');
  const debouncedSearch = useDebounce(search, 800);
  const [userPosition, setUserPosition] = useState<ICoordinates | undefined>(undefined);

  const [selectedMinPermanencyTimeOption, setSelectedMinPermanencyTimeOption] = useState("HOUR");
  const [selectedMaxPermanencyTimeOption, setSelectedMaxPermanencyTimeOption] = useState("HOUR");
  const [minKmInputValue, setMinKmInputValue] = useState("0");
  const [maxKmInputValue, setMaxKmInputValue] = useState("0");
  const [minTimeInputValue, setMinTimeInputValue] = useState("0");
  const [maxTimeInputValue, setMaxTimeInputValue] = useState("0");
  const [areaName, setAreaName] = useState("");

  const [searchParams, setSearchParams] = useSearchParams();
  const location = useLocation();
  const navigate = useNavigate();
  const activeTab = searchParams.get("tab") || "1";

  const [isMaxVelocity, setIsMaxVelocity] = useState(false);
  const [isMaxTime, setIsMaxTime] = useState(false);

  const kmInputRef = useRef<HTMLInputElement>(null);
  const timeInputRef = useRef<HTMLInputElement>(null);

  const minKmHandlers = createKmInputHandlers(setMinKmInputValue, "", () => {
  });
  const maxKmHandlers = createKmInputHandlers(setMaxKmInputValue, minKmInputValue, setIsMaxVelocity, kmInputRef);

  const minTimeInputHandlers = useMemo(() => {
    return createTimeInputTextHandlers(
      setMinTimeInputValue,
      selectedMinPermanencyTimeOption,
      maxTimeInputValue,
      setIsMaxTime,
      timeInputRef,
      false
    );
  }, [selectedMinPermanencyTimeOption, maxTimeInputValue]);

  const maxTimeInputHandlers = createTimeInputTextHandlers(
    setMaxTimeInputValue,
    selectedMaxPermanencyTimeOption,
    minTimeInputValue,
    setIsMaxTime,
    timeInputRef,
    true
  );

  const extractTimeUnit = (value: string): "minutos" | "horas" | "dias" | undefined => {

    const match = value.trim().toLowerCase().match(/(minuto|minutos|hora|horas|dia|dias)/i);
    if (!match) return undefined;

    const unit = match[0];

    if (unit.includes("minuto")) {
      setSelectedMinPermanencyTimeOption("MINUTE");
      setSelectedMaxPermanencyTimeOption("MINUTE");
    }
    if (unit.includes("hora")) {
      setSelectedMinPermanencyTimeOption("HOUR");
      setSelectedMaxPermanencyTimeOption("HOUR");
    }
    if (unit.includes("dia")) {
      setSelectedMinPermanencyTimeOption("DAY");
      setSelectedMaxPermanencyTimeOption("DAY");
    }

    return undefined;
  };

  const shapeOptions = [
    { value: "CIRCLE", label: "Área circular" },
    { value: "POLYGON", label: "Área poligonal" },
  ];

  const typeOptions = [
    { value: "INTEREST", label: "Área de interesse" },
    { value: "AVOID", label: "Área a ser evitada" },
  ];

  const permanencyTypeOptions = [
    { id: "HOUR", label: "Horas" },
    { id: "MINUTE", label: "Minutos" },
    { id: "DAY", label: "Dias" },
  ];

  useEffect(() => {
    const newSearchParams = new URLSearchParams(location.search);
    newSearchParams.set("tab", "1");

    navigate(
      {
        pathname: location.pathname,
        search: newSearchParams.toString(),
      },
      { replace: true }
    );
  }, []);

  useEffect(() => {
    const formatUnit = (value: string, unit: string): string => {
      const numericValue = parseInt(value.replace(/\D/g, ""), 10);
      if (isNaN(numericValue)) return "";
      return `${numericValue} ${unit === "HOUR" ? "horas" : unit === "MINUTE" ? "minutos" : "dias"
        }`;
    };

    if (minTimeInputValue !== "") {
      setMinTimeInputValue(formatUnit(minTimeInputValue, selectedMinPermanencyTimeOption));
    }

    if (maxTimeInputValue !== "") {
      setMaxTimeInputValue(formatUnit(maxTimeInputValue, selectedMaxPermanencyTimeOption));
    }
  }, [selectedMinPermanencyTimeOption, selectedMaxPermanencyTimeOption]);

  const initialData = async (
    pageToSearch = 1,
    signal?: AbortSignal,
    prioritizeRouteIds?: string[],
    isLoadMore = false
  ) => {
    try {
      if (isLoadMore) {
        setIsLoadingMore(true);
      }
      
      const res = await fetchRoutesList(
        pageToSearch,
        PAGE_SIZE,
        debouncedSearch,
        'DESC',
        'createdAt',
        {
          signal,
          prioritizeIds: prioritizeRouteIds ?? selectedRouteIds,
          routeAreaId: areaId
        }
      );

      if (!signal?.aborted && res.data.result.routes) {
        if (isLoadMore) {
          setRoutes(prev => [...prev, ...res.data.result.routes]);
        } else {
          setRoutes(res.data.result.routes);
        }
        setPages(res.data.result.pages);
      }
    } catch (error) {
      if ((error as any).name !== 'CanceledError') {
        Notification.error('Não foi possível buscar as rotas.');
      }
    } finally {
      setPage(pageToSearch);
      setIsLoadingMore(false);
    }
  };

  const fetchAreaDetails = async (id: string) => {
    try {
      const data = await getAreaReworkById(id);

      setAreaName(data.name);
      setType(data.interestType);
      setShape(data.geometryType as string);
      setSelectedRouteIds(data.routeIds ?? []);

      await new Promise(resolve => setTimeout(resolve, 0));

      await initialData(1, undefined, data.routeIds, false);
      if (data.geometryType as string === "CIRCULO") {
        setShape("CIRCLE");
        handleChangeRadius(data.circleRadius ?? 500);
      } else {
        setShape("POLYGON");
      }
      setSelectedOption(data.scope === "GLOBAL" ? 2 : 1);
      setFormattedAddress(formatCoordinates(data.points[0]) || "");
      setMinKmInputValue(data.velocityMin?.toString() || "");
      setMaxKmInputValue(data.velocityMax?.toString() || "");

      setMinTimeInputValue(data.stayTimeMin?.toString() || "");
      setMaxTimeInputValue(data.stayTimeMax?.toString() || "");

      if (data.stayTimeMin != undefined && data.stayTimeMax != undefined) {
        extractTimeUnit(data.stayTimeMin.toString());
      }

      setDrawingPoints(data.points);

      if (data.geometryType === "CIRCULO") {
        setDrawingType("CIRCLE");
        setShape("CIRCLE");
        setCenterMarker(data.points[data.points.length - 1]);
        setCircleDrawing({
          center: data.points[data.points.length - 1],
          radius: data.circleRadius ?? 500,
          type: data.interestType === "AVOID" ? "avoid" : "interest",
        });
      } else {
        setDrawingType("POLYGON");
        setShape("POLYGON");
        setPolygonDrawing({
          points: data.points,
          type: data.interestType === "AVOID" ? "avoid" : "interest",
        });
      }

      Notification.success("Área carregada com sucesso.");
    } catch (error) {
      Notification.error("Erro ao abrir os dados da área.");
    }
  }


  useEffect(() => {
    const controller = new AbortController();
    const signal = controller.signal;

    setPage(1);
    initialData(1, signal, undefined, false);

    return () => controller.abort();
  }, [debouncedSearch]);

  const handleLoadMoreRoutes = (params: {page: number}) => {
    if (!isLoadingMore && params.page > page) {
      initialData(params.page, undefined, undefined, true);
    }
  };

  useEffect(() => {
    setMapZoom(13);
    if (areaId) {
      fetchAreaDetails(areaId);
    } else {
      setDefaultValues();
    }
  }, [areaId]);

  const handleTabChange = (tabKey: string) => {
    const newParams = new URLSearchParams(location.search);
    newParams.set("tab", tabKey);
    navigate(`${location.pathname}?${newParams.toString()}`);
  };

  const handleRouteCheckboxChange = (routeId: string, checked: boolean) => {
    setSelectedRouteIds((prev) => {
      const normalized = new Set(prev);

      if (checked) {
        normalized.add(routeId);
      } else {
        normalized.delete(routeId);
      }

      const updated = Array.from(normalized);

      return updated;
    });
  };

  const handleClearDrawing = () => {
    setCircleDrawing(undefined);
    setPolygonDrawing(undefined);
    setDrawingPoints([]);
    setCenterMarker(null);
    handleChangeRadius(500);
    setFormattedAddress("");
  };

  const setDefaultValues = () => {
    setAreaName("");
    setShape("CIRCLE");
    setType("AVOID");
    setSelectedRouteIds([]);
    setDrawingType("CIRCLE");
    setMinKmInputValue("0");
    setMaxKmInputValue("0");
    setMinTimeInputValue("0");
    setMaxTimeInputValue("0");
    setCircleDrawing(undefined);
    setPolygonDrawing(undefined);
    setDrawingPoints([]);
    setCenterMarker(null);
    handleChangeRadius(500);
    setFormattedAddress("");
  }

  const handleCloseSidebar = () => {
    setDefaultValues();
    onCancel(true);
  };

  const formatCoordinates = (coords: ICoordinates): string => {
    const format = (value: number, isLat: boolean): string => {
      const abs = Math.abs(value).toFixed(4);
      const dir = isLat
        ? value >= 0 ? "N" : "S"
        : value >= 0 ? "E" : "W";
      return `${abs}° ${dir}`;
    };

    return `${format(coords.lat, true)} - ${format(coords.lng, false)}`;
  };

  useEffect(() => {
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          setUserPosition({
            lat: position.coords.latitude,
            lng: position.coords.longitude,
          });
        },
        (error) => {
          console.error("Erro ao obter localização:", error);
          Notification.warning("Não foi possível obter sua localização.");
        }
      );
    } else {
      Notification.warning("Geolocalização não é suportada neste navegador.");
    }
  }, []);

  useEffect(() => {
    if (circleDrawing) {
      setCircleDrawing(prev => prev ? {
        ...prev,
        radius: radius
      } : undefined);
    }
  }, [radius]);

  useEffect(() => {
    if (drawingType !== "CIRCLE") return;
    if (drawingPoints.length === 0) {
      setCircleDrawing(undefined);
      return;
    }

    const lastPoint = drawingPoints[drawingPoints.length - 1];

    setCircleDrawing({
      center: lastPoint,
      radius,
      type: type === "AVOID" ? "avoid" : "interest",
    });
    if (drawingPoints.length > 1) {
      setDrawingPoints([lastPoint]);
    }
  }, [drawingPoints, drawingType, radius, type]);

  useEffect(() => {
    if (drawingType !== "POLYGON") return;

    if (drawingPoints.length >= 3) {
      setPolygonDrawing({
        points: drawingPoints,
        type: type === "AVOID" ? "avoid" : "interest",
      });
    } else {
      setTimeout(() => {
        setPolygonDrawing(undefined);
      }, 0);
    }
  }, [drawingPoints, drawingType, type]);

  const getSelectedClass = (isSelected: boolean) =>
    isSelected ? '' : '';

  const handleSaveArea = async () => {
    try {

      if (!areaName || drawingPoints.length === 0 || !shape || !type) {
        Notification.warning("Área deve conter um nome e pontos definidos");
        return;
      }

      if (shape === "POLYGON" && drawingPoints.length < 3) {
        Notification.warning("O polígono deve conter no mínimo 3 pontos.");
        return;
      }

      if (radius <= 0) {
        Notification.warning("O raio deve ser maior que zero.");
        return;
      }

      const areaTypeId = shape === "POLYGON" ? 1 : 2;

      const defaultTimes = {
        velocityMin: minKmInputValue ? parseInt(minKmInputValue, 10) : 0,
        velocityMax: maxKmInputValue ? parseInt(maxKmInputValue, 10) : 0,
        stayTimeMin: minTimeInputValue ? parseInt(minTimeInputValue, 10) : 0,
        stayTimeMax: maxTimeInputValue ? parseInt(maxTimeInputValue, 10) : 0,
        stayTimeTypeMin: selectedMinPermanencyTimeOption || "MINUTE",
        stayTimeTypeMax: selectedMaxPermanencyTimeOption || "MINUTE",
      };

      const commonPayload = {
        name: areaName,
        areaTypeId,
        interestType: type as "AVOID" | "INTEREST",
        points: drawingPoints,
        routeIds: selectedOption === 1 ? selectedRouteIds : [],
        scope: selectedOption === 2 ? "GLOBAL" : "OPCIONAL",
      };

      let payload: CreateAreaReworkPayload;

      if (shape === "CIRCLE") {
        payload = {
          ...commonPayload,
          circleRadius: radius,
          ...defaultTimes,
        };
      } else if (shape === "POLYGON") {
        payload = {
          ...commonPayload,
          ...defaultTimes,
        };
      } else {
        Notification.error("Formato da área inválido.");
        return;
      }

      await createAreaRework(payload);
      Notification.success("Área criada com sucesso!");
      onCancel(true);
    } catch (error) {
      Notification.error("Erro ao criar a área.");
    }
  };

  const handleUpdateArea = async () => {
    try {

      if (!areaName || drawingPoints.length === 0 || !shape || !type) {
        Notification.warning("Preencha todos os campos obrigatórios.");
        return;
      }

      const areaTypeId = shape === "POLYGON" ? 1 : 2;

      const defaultTimes = {
        velocityMin: minKmInputValue ? parseInt(minKmInputValue, 10) : 0,
        velocityMax: maxKmInputValue ? parseInt(maxKmInputValue, 10) : 0,
        stayTimeMin: minTimeInputValue ? parseInt(minTimeInputValue, 10) : 0,
        stayTimeMax: maxTimeInputValue ? parseInt(maxTimeInputValue, 10) : 0,
        stayTimeTypeMin: selectedMinPermanencyTimeOption || "MINUTE",
        stayTimeTypeMax: selectedMaxPermanencyTimeOption || "MINUTE",
      };

      const commonPayload = {
        name: areaName,
        areaTypeId,
        interestType: type as "AVOID" | "INTEREST",
        points: drawingPoints,
        routeIds: selectedOption === 1 ? selectedRouteIds : [],
        scope: selectedOption === 2 ? "GLOBAL" : "OPCIONAL",
        geometryType: shape,
      };

      const payload: CreateAreaReworkPayload = shape === "CIRCLE"
        ? { ...commonPayload, circleRadius: radius, ...defaultTimes }
        : { ...commonPayload, ...defaultTimes };

      await updateAreaRework(Number(areaId), payload);
      Notification.success("Área atualizada com sucesso!");
      onCancel(true);
    } catch (error) {
      Notification.error("Erro ao atualizar a área.");
    }
  };

  const handleDrawing = (index: number) => {
    setDrawingPoints(prev => prev.filter((_, i) => i !== index));
  }

  const renderTabContent = () => {
    switch (activeTab) {
      case "1":
        return (
          <div>
            <div className=" display-flex flex-column margin-top-15">
              <div className="">
                <label htmlFor="defaultInput" className="display-flex flex-wrap justify-content-between">
                  Nome
                </label>
                <input
                  id="defaultInput"
                  type="text"
                  className="form-control"
                  value={areaName}
                  onChange={(e) => setAreaName(e.target.value)}
                />
              </div>

              <div className="display-flex gap-20 margin-top-25">
                <div className="width-50pct">
                  <label htmlFor="select">Formato</label>
                  <CustomInputSelect
                    className="display-block margin-bottom-5"
                    options={shapeOptions}
                    value={shape}
                    onChange={(selectedValue) => {
                      const type = String(selectedValue);
                      setShape(type);
                      setDrawingType(type === "CIRCLE" || type === "POLYGON" ? type : null);
                      setDrawingPoints([]);
                      setCenterMarker(null);
                      setCircleDrawing(undefined);
                      setPolygonDrawing(undefined);
                      setFormattedAddress("");
                      handleChangeRadius(500);
                    }}
                  />
                </div>
                <div className="width-50pct">
                  <label htmlFor="select">Tipo</label>
                  <CustomInputSelect
                    className="display-block margin-bottom-5"
                    options={typeOptions}
                    value={type}
                    onChange={(selectedValue) => {
                      setType(String(selectedValue))
                      setDrawingPoints([]);
                      setCenterMarker(null);
                      setCircleDrawing(undefined);
                      setPolygonDrawing(undefined);
                      setFormattedAddress("");
                      handleChangeRadius(500);
                    }}
                  />
                </div>
              </div>

              <div className='display-flex margin-top-25'>
                <RadioButton
                  name='stackedRadioBoxes'
                  custom
                  className={`padding-5 rounded-top-left rounded-top-right no-border ${getSelectedClass(
                    selectedOption === 1
                  )}`}
                  onChange={() => setSelectedOption(1)}
                  checked={selectedOption === 1}
                >
                  <div className='radio-text-wrapper display-flex'>
                    <span className='radio-text' />
                    <div className='margin-left-5 margin-right-5'>
                      <div className='text-medium'>Específica</div>
                      <div className='text-color-dark'>Áreas de uso específicas são atribuídas somente a rotas específicas.
                      </div>
                    </div>
                  </div>
                </RadioButton>

                <RadioButton
                  name='stackedRadioBoxes'
                  custom
                  className={`padding-5 rounded-top-left rounded-top-right no-border ${getSelectedClass(selectedOption === 2)}`}
                  onChange={() => setSelectedOption(2)}
                  checked={selectedOption === 2}
                >
                  <div className='radio-text-wrapper display-flex'>
                    <span className='radio-text' />
                    <div className='margin-left-5 margin-right-5'>
                      <div className='text-medium'>Todas as rotas</div>
                      <div className='text-color-dark'>Áreas definidas para uso global são atribuidas a todas as rotas.</div>
                    </div>
                  </div>
                </RadioButton>
              </div>

              {type === "INTEREST" && (
                <>
                  <div className="display-flex gap-15 margin-top-15">
                    <div className="velocity-item">
                      <label htmlFor="areaName" className="dynamic-content-dialog-label">Velocidade mínima</label>
                      <input
                        type="text"
                        className="input"
                        value={minKmInputValue}
                        onChange={minKmHandlers.handleChange}
                        onBlur={minKmHandlers.handleBlur}
                        onKeyDown={minKmHandlers.handleKeyDown}
                      />
                    </div>
                    <div className="velocity-item">
                      <label htmlFor="areaName" className="dynamic-content-dialog-label">Velocidade máxima</label>
                      <input
                        type="text"
                        className={`input-base ${isMaxVelocity ? "input-error" : "input"} `}
                        value={maxKmInputValue}
                        onChange={maxKmHandlers.handleChange}
                        onBlur={maxKmHandlers.handleBlur}
                        onKeyDown={maxKmHandlers.handleKeyDown}
                        ref={kmInputRef}
                      />
                    </div>
                  </div>

                  <div className="display-flex gap-15 margin-top-25">
                    <div className="display-flex gap-5">
                      <div className="display-flex flex-column">
                        <label className="dynamic-content-dialog-label margin-bottom-5 width-100pct ">
                          Tempo de permanência mínimo
                        </label>
                        <input
                          type="text"
                          className="input width-100pct"
                          value={minTimeInputValue}
                          onChange={minTimeInputHandlers.handleChange}
                          onBlur={minTimeInputHandlers.handleBlur}
                          onKeyDown={minTimeInputHandlers.handleKeyDown}
                        />
                      </div>
                      <div className="align-self-end">

                        <Select
                          id='1'
                          placeholder={''}
                          value={[selectedMinPermanencyTimeOption]}
                          options={permanencyTypeOptions}
                          onChange={(item) => {
                            setSelectedMinPermanencyTimeOption(item?.id ?? "");
                          }}
                          className='test-className width-100'
                          btnClassName='test-btnClassName'
                          dropdownClassName='test-dropdownClassName'
                        />
                      </div>
                    </div>
                    <div className="display-flex gap-5">
                      <div className="display-flex flex-column">
                        <label htmlFor="areaName" className="dynamic-content-dialog-label">Tempo de permanencia
                          máximo
                        </label>
                        <input
                          type="text"
                          className={`input-base ${isMaxTime ? "input-error" : "input"}`}
                          value={maxTimeInputValue}
                          onChange={maxTimeInputHandlers.handleChange}
                          onBlur={maxTimeInputHandlers.handleBlur}
                          onKeyDown={maxTimeInputHandlers.handleKeyDown}
                          ref={timeInputRef}
                        />
                      </div>
                      <div className="align-self-end ">
                        <Select
                          id='1'
                          placeholder=''
                          options={permanencyTypeOptions}
                          value={[selectedMaxPermanencyTimeOption]}
                          onChange={(item) => {
                            setSelectedMaxPermanencyTimeOption(item?.id ?? "");
                          }}
                          className='test-className width-100'
                          btnClassName='test-btnClassName'
                          dropdownClassName='test-dropdownClassName'
                        />
                      </div>
                    </div>
                  </div>
                </>
              )}
              <div className="margin-top-25">
                <label htmlFor="enderecoInput" className="display-flex flex-wrap">
                  {shape === "POLYGON" ? "Mapa do Centro" : "Endereço"}
                </label>
                <Autosuggest
                  fetchData={async (text: string) => {
                    const response = await fetchAutosuggest(text);
                    return response?.map((i) => ({ label: i.title, description: i.address.label, ...i } as any));
                  }}
                  placeholder="Informe o centro do mapa"
                  icon="rioglyph-finish"
                  defaultValue={formattedAddress}
                  onClear={() => {
                    handleClearDrawing();
                  }}
                  onSuggestionSelected={(e, i) => {
                    const coordinates = i.suggestion.position as unknown as ICoordinates;
                    if (shape === "CIRCLE") {
                      setCircleDrawing({
                        center: coordinates,
                        radius: radius,
                        type: type === "AVOID" ? "avoid" : "interest",
                      });
                    }
                    setDrawingPoints([coordinates]);
                    setCenterMarker(coordinates);
                    setMapCenter(coordinates);
                    setFormattedAddress(formatCoordinates(coordinates));
                  }}
                />
              </div>
              {shape === "CIRCLE" && (
                <>
                  <div className="display-flex flex-column margin-top-25">
                    <label>Raio da área</label>
                    <div className='display-flex align-items-center'>
                      <Slider
                        value={radius}
                        minValue={0}
                        maxValue={80000}
                        step={1}
                        onDragEnd={() => {
                        }}
                        onChange={handleChangeRadius}
                      />
                      <div className='width-100 margin-left-10'>
                        <input
                          className='form-control text-center no-controls'
                          type='text'
                          min={0}
                          max={80000}
                          value={`${radius} m`}
                          onChange={(e) => {
                            const cleaned = e.target.value.replace(/\D/g, "");
                            const numeric = parseInt(cleaned, 10);
                            const parsed = isNaN(numeric) ? 0 : numeric;

                            handleChangeRadius(parsed);
                          }}
                        />
                      </div>
                    </div>
                  </div>
                  <div className="display-flex align-items-center margin-top-5 padding-5" style={{ backgroundColor: "#EEF4F9" }}>
                    <div
                      className="bg-gray display-flex align-items-center justify-content-center rounded-circle margin-right-10 margin-left-15">
                      <span className="rioglyph rioglyph-info text-size-12 text-color-white padding-2" />
                    </div>
                    <span
                      className="text-color-gray">Segurando e arrastando, movimente a posição do circulo no mapa </span>
                  </div>
                </>
              )}
              {shape === "POLYGON" && (
                <>
                  <div className="display-flex align-items-center margin-top-15 padding-5" style={{ backgroundColor: "#EEF4F9" }}>
                    <div
                      className="bg-gray display-flex align-items-center justify-content-center rounded-circle margin-right-10 margin-left-15">
                      <span className="rioglyph rioglyph-info text-size-12 text-color-white padding-2" />
                    </div>
                    <span
                      className="text-color-gray text-size-12 padding-right-5 margin-right-5">Para criar o polígono, clique no mapa à volta do
                      perímetro pretendido. Os novos pontos são sempre adicionados à aresta mais próxima.
                      Pode alterar a forma do polígono arrastando os pontos.
                    </span>
                  </div>
                  <div className="display-flex flex-column margin-top-25 padding-left-25">
                    <span className="text-color-gray text-size-12">Pontos Selecionados</span>

                    {drawingPoints.map((point, index) => (
                      <div key={index} className="display-flex align-items-center margin-top-5">
                        <span className="rioglyph rioglyph-layer text-color-darker text-size-20" />
                        <span className="text-color-darker text-size-14 margin-left-10">
                          {formatCoordinates(point)}
                        </span>
                        <button
                          type="button"
                          className="btn btn-primary btn-link btn-icon-only margin-left-auto"
                          onClick={() => handleDrawing(index)}
                        >
                          <span className="rioglyph rioglyph-trash" aria-hidden="true" />
                        </button>
                      </div>
                    ))}
                  </div>

                </>
              )}
              <button type="button" className="btn btn-default width-20pct margin-top-20 align-self-end"
                onClick={handleClearDrawing}>
                <span className="" aria-hidden="true"></span>
                {shape === "CIRCLE" ? "Excluir ponto" : "Excluir pontos"}
              </button>
            </div>
          </div>
        );
      case "2":
        return (
          <>
            <div className="margin-top-15">
              <TableSearch
                value={search}
                onChange={(value) => setSearch(value)}
                placeholder="Buscar em rotas"
              />
            </div>

            <div 
              className="margin-top-15 margin-bottom-15 overflow-y-auto max-height-400 padding-10 border rounded"
            >
              {routes.map((route) => (
                <div
                  key={route.id}
                  className="display-flex align-items-center padding-y-10 border border-style-solid border-width-1 border-bottom-only border-left-none border-right-none border-top-none"
                >
                  <div className="margin-right-20">
                    <Checkbox
                      checked={selectedRouteIds.includes(route.id)}
                      onChange={(e) =>
                        handleRouteCheckboxChange(route.id, e.target.checked)
                      }
                      label=""
                    />
                  </div>
                  <span className="text-size-14 text-color-darker">{route.name}</span>
                  <span className="text-size-20 text-color-gray rioglyph rioglyph-route-option margin-left-auto"></span>
                </div>
              ))}
            </div>

            <div className="overflow-x-auto">
              <Paginator
                handlePage={handleLoadMoreRoutes}
                page={page}
                pages={pages}
                pageSize={PAGE_SIZE}
                disabled={routes.length === 0}
              />
            </div>
          </>
        );
    }
  }

  useEffect(() => {
    if (!searchParams.get("tab")) {
      const newSearchParams = new URLSearchParams(location.search);
      newSearchParams.set("tab", "1");

      navigate(
        {
          pathname: location.pathname,
          search: newSearchParams.toString(),
        },
        { state: location.state }
      );
    }
  }, []);

  const getMarkerConfig = (type: string): { iconName: string, text: string, color: MarkerColor } => ({
    iconName: type === "AVOID" ? "rioglyph-error-sign" : "rioglyph-heart",
    text: type === "AVOID" ? "Área a ser evitada" : "Área de interesse",
    color: type === "AVOID" ? "bg-map-marker-danger" : "bg-map-marker-success"
  });

  const createMarkerName = (config: ReturnType<typeof getMarkerConfig>, coordinates: any) => (
    <div className="display-flex align-items-center gap-10 padding-3 margin-x-3">
      <span className={`text-anchorSize-h4 ${config.iconName}`} />
      <div className="line-height-16">
        <div className="text-medium">{config.text}</div>
        <div className="text-anchorSize-12">{formatCoordinates(coordinates)}</div>
      </div>
    </div>
  );

  const createMarkerProps = (config: ReturnType<typeof getMarkerConfig>, coordinates: any) => ({
    iconNames: [config.iconName],
    name: createMarkerName(config, coordinates),
    markerColor: config.color,
    markerOnHover: true,
    anchorSize: "lg" as const,
  });

  const createMarker = (id: string, position: any, type: string): ISandboxMarker => ({
    id,
    position,
    type: "addressType",
    markerProps: createMarkerProps(getMarkerConfig(type), position),
  });

  const createCircleMarkers = (drawingPoints: any[], type: string): ISandboxMarker[] => {
    const lastPoint = drawingPoints[drawingPoints.length - 1];
    return [createMarker("circle-center-marker", lastPoint, type)];
  };

  const createPolygonMarkers = (drawingPoints: any[], type: string): ISandboxMarker[] => {
    return drawingPoints.map((point, index) =>
      createMarker(`polygon-point-${index}`, point, type)
    );
  };

  const getDrawingMarkers = (drawingType: "CIRCLE" | "POLYGON" | null, drawingPoints: any[], type: string): ISandboxMarker[] => {
    if (!drawingType || drawingPoints.length === 0) {
      return [];
    }

    if (drawingType === "CIRCLE") {
      return createCircleMarkers(drawingPoints, type);
    }

    if (drawingType === "POLYGON") {
      return createPolygonMarkers(drawingPoints, type);
    }

    return [];
  };

  const drawingMarkers: ISandboxMarker[] = getDrawingMarkers(drawingType, drawingPoints, type);

  useEffect(() => {
    setEnableDrawingClick(true);
    return () => {
      setEnableDrawingClick(false);
    };
  }, []);

  return (
    <>
      <ApplicationLayout.Sidebar className="right z-index-2 scroll-x-none height-100pct">
        <Sidebar
          title={areaId ? "Editar área" : "Criar área"}
          closed={false}
          onClose={handleCloseSidebar}
          position={Sidebar.RIGHT}
          width={660}
        >
          <div className="padding-20 margin-right-20">
            <SubNavigation
              className="padding-0 margin-0 border border-bottom-only border-width-1"
              navItems={[
                {
                  key: "1",
                  route: (
                    <NavLink
                      style={{
                        padding: "10px 20px",
                        margin: 0,
                        textAlign: "center",
                      }}
                      to="?tab=1"
                      onClick={(e) => {
                        e.preventDefault();
                        handleTabChange("1");
                      }}
                      className={() => (searchParams.get("tab") === "1" ? "active" : "")}
                    >
                      CRIAR ÁREA
                    </NavLink>
                  ),
                },
                ...(selectedOption === 1
                  ? [
                    {
                      key: "2",
                      route: (
                        <NavLink
                          to="?tab=2"
                          style={{
                            padding: "10px 20px",
                            margin: 0,
                            textAlign: "center",
                          }}
                          onClick={(e) => {
                            e.preventDefault();
                            handleTabChange("2");
                          }}
                          className={() => (searchParams.get("tab") === "2" ? "active" : "")}
                        >
                          ROTAS SELECIONADAS
                        </NavLink>
                      ),
                    },
                  ]
                  : []),
              ]}
            />
            {renderTabContent()}
            <div className="border border-style-solid border-color-gray border-width-1 border-top-only margin-top-25">
              <button type="button" className="btn btn-primary width-100pct margin-top-25" onClick={() => areaId ? handleUpdateArea() : handleSaveArea()}>
                <span className="" aria-hidden="true">
                </span>
                <span className="text-capitalize">{"Salvar"}</span>
              </button>
            </div>
          </div>

        </Sidebar>
      </ApplicationLayout.Sidebar>

      <ApplicationLayout.Body innerClassName="padding-0" className="height-100pct">
        <RioMap
          position={
            areaId
              ? shape === "CIRCLE" && circleDrawing
                ? circleDrawing.center
                : shape === "POLYGON" && polygonDrawing?.points?.length
                  ? polygonDrawing.points[0]
                  : userPosition
              : userPosition
          }
          boundingBox={undefined}
          apiKey={process.env.REACT_APP_RIO_MAP_KEY as string}
          segments={[]}
          currencySegments={[]}
          markers={drawingMarkers}
          useClusters={false}
        />
      </ApplicationLayout.Body>
    </>
  );
}
export default AreasCreate;